// the engine markup's optional status lines (not every page has them)
function setHtml(id, html) {
    const el = document.getElementById(id);
    if (el) el.innerHTML = html;
}

/*
 * PRODUCT PAGE — switching the variant (main characteristic value, e.g. colour,
 * and the other one, e.g. size) without a page load.
 *
 * Two markups:
 *  - a theme that marks its page with [data-pdp-root] gives only markup; this
 *    module fills it (see pdpContract below):
 *      [data-pdp-root data-title-suffix=" - Shop"]
 *      [data-pdp-name]                         the product name (gets pn-{variant id})
 *      [data-pdp-value="main"|"other"]         the selected values' names (an "other" with data-type-id="{type id}" is that
 *                                              characteristic's; without it the first other characteristic's)
 *      [data-pdp-variant data-cid data-id]     the main value swatches (active class: data-active-class, default "active")
 *      [data-pdp-options] > template[data-pdp-option]   one input + label per option of an other characteristic: a box with
 *                                              data-type-id="{type id}" is that characteristic's, one without it the first one's
 *                                              (a product can be chosen by any number of characteristics: one box each)
 *      [data-pdp-price] > template[data-pdp-price="normal"|"discount"]   with [data-pdp-slot="price"|"original"]
 *      [data-pdp-gallery="thumbs"|"stage"] > template   one element per image (an img inside, or the img itself)
 *      [data-pdp-spec] > template[data-pdp-spec-row]    with [data-pdp-slot="name"|"value"]
 *      [data-pdp-add data-enabled-class data-disabled-class]   the add-to-cart button
 *      [data-pdp-wish]                         the wishlist button (data-wish-id follows the variant)
 *    Elements the server rendered from the variant carry [data-pdp-item]:
 *    they are replaced. After a change "product:variant" fires on document
 *    ({id, product, schemas, shops, blocks}) for the theme's own UI (gallery, dates...); `blocks` are the page's blocks
 *    that depend on the variant (a colour family filter), rendered for it by the server: {block name: html}.
 *  - otherwise the engine's own markup (#gallery swiper, .sizes .d-flex, ...).
 */
const pdpContract = {
    root() { return document.querySelector('[data-pdp-root]'); },
    q(sel) { const r = this.root(); return r ? r.querySelector(sel) : null; },
    qa(sel) { const r = this.root(); return r ? Array.from(r.querySelectorAll(sel)) : []; },
    fromTemplate(tpl) {
        const frag = tpl.content.cloneNode(true);
        Array.from(frag.children).forEach((el) => el.setAttribute('data-pdp-item', ''));
        return frag;
    },
    clear(container) {
        Array.from(container.querySelectorAll(':scope > [data-pdp-item]')).forEach((el) => el.remove());
    },
    slot(el, name, text) {
        const s = el.querySelector('[data-pdp-slot="' + name + '"]');
        if (s) s.textContent = text ?? '';
    },
    setValue(which, text, typeId = null) {
        // an element with a type id shows that characteristic's value; one without it the first one's (typeId null)
        const sel = typeId === null
            ? '[data-pdp-value="' + which + '"]:not([data-type-id])'
            : '[data-pdp-value="' + which + '"][data-type-id="' + typeId + '"]';
        this.qa(sel).forEach((el) => { el.textContent = text; });
    },
    apply(id, data, main, other, others = other ? [other] : []) {
        const product = data.product;
        // names of the selected values (tracking reads #size / #color too)
        this.setValue('main', main?.selected?.name ?? '');
        this.setValue('other', other?.selected?.name ?? '');
        for (const o of others) {
            this.setValue('other', o?.selected?.name ?? '', o.type_id);
        }

        // main value swatches: active one, and each swatch links to its variant of the new selection
        this.qa('[data-pdp-variant]').forEach((el) => {
            const cls = el.dataset.activeClass || 'active';
            el.classList.toggle(cls, String(el.dataset.cid) === String(main?.selected?.c_id));
        });
        for (const c of Object.values(main?.variants ?? {})) {
            const el = this.q('[data-pdp-variant][data-cid="' + c.c_id + '"]');
            if (el) el.dataset.id = c.id;
        }

        // the product name keeps the pn-{id} class the header cart reads
        this.qa('[data-pdp-name]').forEach((el) => {
            Array.from(el.classList).filter((c) => c.startsWith('pn-')).forEach((c) => el.classList.remove(c));
            el.classList.add('pn-' + id);
        });

        // options of the other characteristics: one box each
        let anyPreorder = false;
        let hasOptionBox = false;
        this.qa('[data-pdp-options]').forEach((box) => {
            const characteristic = box.dataset.typeId
                ? others.find((c) => String(c.type_id) === String(box.dataset.typeId))
                : other;
            const optTpl = box.querySelector('template[data-pdp-option]');
            if (!optTpl) return;
            hasOptionBox = true;
            this.clear(box);
            const disabledClass = box.dataset.disabledClass || 'disabled';
            // a méret saját előrendelése (ProductController::findActiveSizes); régi válasznál a változaté
            const preorderClass = box.dataset.preorderClass || '';
            for (const o of Object.values(characteristic?.variants ?? {})) {
                const frag = this.fromTemplate(optTpl);
                const input = frag.querySelector('input');
                const label = frag.querySelector('label');
                const domId = (box.dataset.idPrefix || 'size-') + o.c_id;
                if (input) {
                    input.id = domId;
                    input.value = o.name;
                    input.dataset.id = o.id;
                    input.checked = String(o.c_id) === String(characteristic?.selected?.c_id);
                    input.addEventListener('change', (e) => { if (e.currentTarget.checked) productPage.loadAjax(o.id); });
                }
                if (label) {
                    label.setAttribute('for', domId);
                    label.textContent = o.name;
                    const pre = o.preorder !== undefined ? !!o.preorder : product.preorder == 1;
                    label.classList.toggle(disabledClass, o.stock < 1 && !pre);
                    if (preorderClass) label.classList.toggle(preorderClass, o.stock < 1 && pre);
                    if (o.stock < 1 && pre) anyPreorder = true;
                }
                box.appendChild(frag);
            }
        });
        if (hasOptionBox) {
            // the line under the options that explains the pre-order marking: there while one option is pre-orderable
            const preNote = this.q('[data-pdp-pre-note]');
            if (preNote) preNote.hidden = !anyPreorder;
        }

        // price
        const price = this.q('[data-pdp-price]');
        if (price) {
            const discount = product.product_discount_price > 0;
            const tpl = price.querySelector('template[data-pdp-price="' + (discount ? 'discount' : 'normal') + '"]');
            if (tpl) {
                this.clear(price);
                const frag = this.fromTemplate(tpl);
                const wrap = document.createElement('div');
                wrap.appendChild(frag);
                this.slot(wrap, 'price', discount ? product.formatted_product_discount_price : product.formatted_price);
                this.slot(wrap, 'original', product.formatted_price);
                Array.from(wrap.children).forEach((el) => price.appendChild(el));
            }
        }

        // the images' alt: "{name} – {colour}", and " – {n}. image" when there are several (the pattern comes from the page:
        // data-alt-image="Image :n", in the page's language)
        const lanKey = window.lan;
        const productName = (product.product && (product.product.title?.[lanKey] ?? product.product.title?.en ?? product.product.name?.[lanKey] ?? product.product.name?.en)) || '';
        const baseAlt = [productName, main?.selected?.name].filter((x) => x && String(x).trim()).join(' – ');
        const altPattern = (this.root() && this.root().dataset.altImage) || '';
        const imageCount = (product.productImages || []).length;
        const altOf = (i) => (imageCount > 1 && altPattern ? baseAlt + ' – ' + altPattern.replace(':n', String(i + 1)) : baseAlt);

        // gallery: one thumb and one stage image per variant image
        ['thumbs', 'stage'].forEach((part) => {
            const el = this.q('[data-pdp-gallery="' + part + '"]');
            const tpl = el && el.querySelector('template');
            if (!el || !tpl) return;
            this.clear(el);
            (product.productImages || []).forEach((image, i) => {
                const frag = this.fromTemplate(tpl);
                const node = frag.firstElementChild;
                const img = node && (node.tagName === 'IMG' ? node : node.querySelector('img'));
                if (img) {
                    img.src = image.src;
                    img.alt = altOf(i);
                    if (image.srcset) img.srcset = image.srcset;
                    if (part === 'stage') {
                        if (i === 0) { img.fetchPriority = 'high'; img.classList.add('pi-' + id); }
                        else img.loading = 'lazy';
                    }
                }
                if (node && i === 0 && el.dataset.firstClass) node.classList.add(el.dataset.firstClass);
                el.appendChild(frag);
            });
        });

        // spec rows from the variant's schemas
        const spec = this.q('[data-pdp-spec]');
        const rowTpl = spec && spec.querySelector('template[data-pdp-spec-row]');
        if (spec && rowTpl) {
            this.clear(spec);
            for (const key of Object.keys(data.schemas || {})) {
                const frag = this.fromTemplate(rowTpl);
                const wrap = document.createElement('div');
                wrap.appendChild(frag);
                this.slot(wrap, 'name', data.schemas[key].name);
                this.slot(wrap, 'value', data.schemas[key].value);
                Array.from(wrap.children).forEach((c) => spec.appendChild(c));
            }
        }

        // add to cart
        this.qa('[data-pdp-add]').forEach((btn) => {
            btn.dataset.id = id;
            btn.dataset.sku = product.sku;
            btn.dataset.serial = product.product.serial;
            btn.dataset.price = product.product_discount_price > 0 ? product.product_discount_price : product.price;
            btn.innerHTML = product.button_text;
            const on = product.availability_button !== 0;
            const enabled = btn.dataset.enabledClass, disabled = btn.dataset.disabledClass;
            if (enabled) btn.classList.toggle(enabled, on);
            if (disabled) btn.classList.toggle(disabled, !on);
            btn.disabled = !on;
        });
        this.qa('[data-pdp-wish]').forEach((btn) => { btn.dataset.wishId = id; });

        // blocks: the page's blocks that depend on the variant (a colour family filter), already rendered for it (block name => HTML)
        document.dispatchEvent(new CustomEvent('product:variant', { detail: { id, product, schemas: data.schemas, shops: data.shops, blocks: data.blocks || {} } }));
    },
};

const productPage = {
    loaded() {
        if (document.getElementById("product-page") !== undefined && document.getElementById("product-page") !== null) {
            setTimeout(() => {
                let btn = document.querySelector(".btn-add-cart");

                if(btn !== null && btn !== undefined) {
                    AppEvents.callEvent("VIEW_CONTENT", {
                        item_id: btn.dataset.id,
                        item_sku: btn.dataset.sku,
                        item_name: document.getElementsByClassName("pn-" + btn.dataset.id)[0]?.innerHTML ?? document.title,
                        item_category: btn.dataset.category_id,
                        item_serial: btn.dataset.serial,
                        currency: currency,
                        price: btn.dataset.price,
                        size: document.getElementById('size')?.innerText ?? null,
                        color: document.getElementById('color')?.innerText ?? null
                    });
                }else{
                    let options = document.getElementsByClassName("item-selector");
                    for (let i = 0; i < options.length; i++) {
                        if(options[i].checked){
                            AppEvents.callEvent("VIEW_CONTENT", {
                                item_id: options[i].dataset.id,
                                item_sku: options[i].dataset.sku,
                                item_name: document.getElementsByClassName("pn-" +options[i].name.replace("size-",""))[0]?.innerHTML ?? document.title,
                                item_category: options[i].dataset.category_id,
                                item_serial:  options[i].dataset.serial,
                                currency: currency,
                                price:  options[i].dataset.price
                            });
                        }
                    }
                }
            }, 500);
        }
    },
    load(first=true) {
        if (pdpContract.root()) {
            if (!first) return;
            // the options the server rendered (the rebuilt ones get their listener in apply)
            pdpContract.qa('[data-pdp-options] input').forEach((input) => {
                input.addEventListener('change', (e) => { if (e.currentTarget.checked) productPage.loadAjax(e.currentTarget.dataset.id); });
            });
            pdpContract.qa('[data-pdp-variant]').forEach((el) => {
                el.addEventListener('click', (e) => {
                    e.preventDefault();
                    productPage.loadAjax(e.currentTarget.dataset.id, true, true);
                });
            });
            return;
        }
        if (document.getElementsByName("size").length > 0) {
            let element = document.getElementsByName("size");
            for(let i=0;i<element.length;i++){
                element[i].addEventListener("change",(event) =>{
                   if(!event.currentTarget.checked){
                       return;
                   }
                   let id = event.currentTarget.dataset.id;
                   productPage.loadAjax(id);
                });
            }
        }
        if (first && document.getElementsByClassName("color-a").length > 0) {
            let element = document.getElementsByClassName("color-a");
            for(let i=0;i<element.length;i++){
                element[i].addEventListener("click",(e) =>{
                    e.preventDefault();
                    let id = e.currentTarget.dataset.id;
                    productPage.loadAjax(id,true,true);
                });
            }
        }

        /* bundle */
        if (document.getElementsByClassName("item-selector").length > 0) {
            let element = document.getElementsByClassName("item-selector");
            for (const selector of element) {
                selector.addEventListener("change",() =>{
                    let availability_button = 1;
                    let availability = "";
                    for (const e of element) {
                        if(!e.checked){
                            continue;
                        }
                        if(availability.length === 0){
                            availability = e.dataset.availability;
                        }
                        if(e.dataset.availability_button == 0){
                            availability_button = 0;
                            availability = e.dataset.availability;
                        }else if(availability_button == 1 && e.dataset.availability_button == 2){
                            availability_button = 2;
                            availability = e.dataset.availability;
                        }
                    }
                    if(availability_button === 1){
                        setHtml("availability", "<div class=\"span round bg-green\"></div>" + availability);
                    } else if(availability_button === 2){
                        setHtml("availability", "<div class=\"span round bg-yellow\"></div>" + availability);
                    }else {
                        setHtml("availability", "<div class=\"span round bg-red\"></div>" + availability);
                    }
                });
            }
        }
    },
    async loadContract(id, addHistory, scrollTop) {
        const root = pdpContract.root();
        const eventType = !scrollTop ? "CUSTOMIZE_PRODUCT" : "VIEW_CONTENT";
        const extra = eventType === "VIEW_CONTENT" ? "?page_load_id=" + window.loadID : "";
        root.classList.add('is-loading');
        let data;
        try {
            const response = await fetch("/api/product/" + id + extra, { headers: http.headers() });
            data = await response.json();
        } catch (e) {
            root.classList.remove('is-loading');
            return;
        }
        root.classList.remove('is-loading');
        if (!data || !data.product) return;
        const lan = window.lan;
        const characteristics = data.product.variant_chatacteristics ?? [];
        const isMain = (c) => c.is_variant == true && (c.main_characteristics === true || c.main_characteristics === 1 || c.main_characteristics === '1');
        const main = characteristics.find(isMain);
        const others = characteristics.filter((c) => c.is_variant == true && !isMain(c));
        const other = others[0];

        // the variant's own URL: the last path segment is the variant id
        const parts = location.pathname.split("/");
        parts[parts.length - 1] = id;
        const name = data.product.product.title?.[lan] ?? data.product.product.title?.en ?? data.product.product.name?.[lan] ?? '';
        const title = [(data.metaText ?? '') + ' ' + name, ...others.map((c) => c?.selected?.name)].filter((x) => x && x.trim()).join(' - ') + (root.dataset.titleSuffix || '');
        if (addHistory) history.pushState({ page: "product", id: id }, title, parts.join("/"));
        document.title = title.trim();

        if (document.body.offsetWidth < 492 && scrollTop) window.scrollTo({ top: 0, behavior: 'smooth' });

        AppEvents.callEvent(eventType, {
            item_id: id,
            item_sku: data.product.sku,
            item_name: data.product.product.name?.[lan],
            item_serial: data.product.product.serial,
            item_category: data.product.product.category_id,
            currency: typeof currency !== 'undefined' ? currency : null,
            price: data.product.price,
            characteristics: characteristics
        });

        pdpContract.apply(id, data, main, other, others);
        productPage.updateMix(data);
    },
    // the set builder section (.mix-box: top + bottom rails) follows the variant
    updateMix(data) {
        if (!data.related || !data.related.bikiniTop || !data.related.bikiniBottom) return;
        let pMix = document.getElementsByClassName("mix-box");
        if(pMix.length>0) {
            pMix = pMix[0];
            let tMix = pMix.getElementsByClassName("products")[0];
            let bMix = pMix.getElementsByClassName("products")[1];

            let tElement = tMix.getElementsByClassName('product')[0].cloneNode(true);
            let bElement = bMix.getElementsByClassName('product')[0].cloneNode(true);

            tMix.innerHTML = "";
            bMix.innerHTML = "";

            for (const item of data.related.bikiniTop) {
                let e = tElement.cloneNode(true);
                e.classList.remove('selected');
                e.dataset.id = item.id;
                e.dataset.sku = item.sku;
                e.dataset.serial = item.product.serial;
                e.dataset.size = item.size?.name;
                e.dataset.price = item.price;
                e.dataset.name = item.product.name;
                e.dataset.img = item.productImages[0];
                e.querySelector(".pure-img").src = item.productImages[0];
                tMix.appendChild(e);
            }
            tMix.dataset.i = Math.floor(data.related.bikiniTop.length / 2);

            for (const item of data.related.bikiniBottom) {
                let e = bElement.cloneNode(true);
                e.classList.remove('selected');
                e.dataset.id = item.id;
                e.dataset.sku = item.sku;
                e.dataset.serial = item.product.serial;
                e.dataset.size = item.size?.name;
                e.dataset.price = item.price;
                e.dataset.name = item.product.name;
                e.dataset.img = item.productImages[0];
                e.querySelector(".pure-img").src = item.productImages[0];
                bMix.appendChild(e);
            }
            bMix.dataset.i = Math.floor(data.related.bikiniBottom.length / 2);
            setTimeout(() => window.scrollx.resize(),100);
        }
    },
    navigate(event) {
        if (event.state && "product" === event.state.page && event.state.id) {
            this.loadAjax(event.state.id,false);
        }else {
            window.location = location.href;
        }
    },
    loadAjax(id,addHistory=true,scrollTop = false) {
        if (pdpContract.root()) {
            return this.loadContract(id, addHistory, scrollTop);
        }
        let btn = document.querySelector(".btn-add-cart");
        btn.dataset.id = id;

        let eventType = !scrollTop ? "CUSTOMIZE_PRODUCT" : "VIEW_CONTENT";
        let extra = "";

        if(eventType == "VIEW_CONTENT") {
            extra = "?page_load_id="+window.loadID;
        }

        fetch("/api/product/"+id+extra, {
            headers: http.headers(),
        })
            .then((response) => response.json())
            .then((data) => {
                const characteristics = data.product.variant_chatacteristics ?? [];
                const isMainCharacteristic = (characteristic) => characteristic.is_variant == true && (characteristic.main_characteristics === true || characteristic.main_characteristics === 1 || characteristic.main_characteristics === '1');
                const colorCharacteristic = characteristics.find(isMainCharacteristic);
                const otherCharacteristics = characteristics.find((characteristic) => characteristic.is_variant == true && !isMainCharacteristic(characteristic));
                const selectedColor = colorCharacteristic?.selected?.name ?? '';
                const selectedValue = otherCharacteristics?.selected?.name ?? '';
                let url = location.pathname.split("/");
                url = url[0]+"/"+url[1]+"/"+url[2]+"/"+url[3]+"/"+url[4]+"/"+id;
                let title = document.title
                    .replace(' - '+document.getElementById('size').innerText,' - '+selectedValue)
                    .replace(document.getElementById('color').innerText,selectedColor);
                if(addHistory) {
                    history.pushState({page: "product", id: id}, title, url);
                }
                document.title = title;
                document.getElementById('size').innerText = selectedValue;
                document.getElementById('color').innerText = selectedColor;

                if (document.body.offsetWidth < 492 && scrollTop) {
                    window.scrollTo({top: 0, behavior: 'smooth'});
                }


                AppEvents.callEvent(eventType, {
                    item_id: id,
                    item_sku: data.product.sku,
                    item_name: data.product.product.name[lang],
                    item_serial: data.product.product.serial,
                    item_category: data.product.product.category_id,
                    currency: currency,
                    price: data.product.price,
                    characteristics: characteristics
                });

                document.querySelector(".color-a.active").classList.remove("active");
                document.querySelector(`.color-a[data-cid="${colorCharacteristic?.selected?.c_id}"]`)?.classList.add("active");

                for (const c of Object.values(colorCharacteristic?.variants ?? {})) {
                    document.querySelector(`.color-a[data-cid="${c.c_id}"]`).dataset.id = c.id;
                }

                document.querySelector(".product-page h1").classList.length = 0;
                document.querySelector(".product-page h1").classList.add("pn-"+id);

                const sElement = document.querySelector(".sizes .d-flex");
                const ie = sElement.querySelector("input");
                const iv = sElement.querySelector("label");

                sElement.innerHTML = "";
                for (const s of Object.values(otherCharacteristics?.variants ?? {})) {
                    let i = ie.cloneNode(true);
                    let v = iv.cloneNode(true);
                    i.dataset.id = s.id;
                    i.value = s.name;
                    i.checked = String(s.c_id) === String(otherCharacteristics?.selected?.c_id);
                    i.id = `size-${s.c_id}`;
                    v.setAttribute("for",`size-${s.c_id}`);
                    v.innerText = s.name;
                    v.className = s.stock < 1 && !(s.preorder !== undefined ? s.preorder : data.product.preorder == 1) ? "disabled" : "";
                    sElement.appendChild(i);
                    sElement.appendChild(v);
                }
                productPage.load(false);
                setHtml("deliveryDate", data.product.deliveryDate);

                if(data.product.availability) {
                    if(data.product.availability_button === 1){
                        setHtml("availability", "<div class=\"span round bg-green\"></div>" + data.product.availability);
                    } else if(data.product.availability_button === 2){
                        setHtml("availability", "<div class=\"span round bg-yellow\"></div>" + data.product.availability);
                    }else {
                        setHtml("availability", "<div class=\"span round bg-red\"></div>" + data.product.availability);
                    }
                }else{
                    setHtml("availability", "");
                }
                let pcs = document.getElementById("gallery").getElementsByTagName("div")[0];
                pcs.style.height = pcs.clientHeight+"px";
                pcs.innerHTML = "";
                let iclass = false;
                for (const s of data.product.productImages) {
                    let e = document.createElement("div");
                    e.className = "product-image swiper-slide";

                    let k = document.createElement("div");
                    k.className = "swiper-zoom-container";

                    let i = document.createElement("img");
                    i.srcset = s.srcset;
                    i.src = s.src;
                    i.sizes = '(min-width: 1480px) 840px,(min-width: 1280px) 720px,(min-width: 1280px) 720px,(min-width: 1024px) 380px,(min-width: 569px) 238px,100vw';
                    i.loading = "lazy";
                    if(!iclass){
                        i.className = "pure-img pi-"+id;
                        iclass = true;
                    }else{
                        i.className = "pure-img";
                    }

                    k.appendChild(i);
                    e.appendChild(k);
                    pcs.appendChild(e);
                }
                setTimeout(() => pcs.style.height = "",300);
                setTimeout(() => window.scrollx.resetSize(),100);
                let btn = document.querySelector(".btn-add-cart");
                btn.dataset.id = id;
                btn.dataset.sku = data.product.sku;
                btn.dataset.serial = data.product.product.serial;
                btn.dataset.price = data.product.product_discount_price > 0 ? data.product.product_discount_price : data.product.price;

                if(data.product.product_discount_price > 0) {
                    document.getElementById('price').innerHTML =
                        '<div class="price-original"><span class="price-amount">' + data.product.formatted_price + '</span></div>'+
                        ' <span class="price-amount">' + data.product.formatted_product_discount_price + '</span>';
                }else{
                    document.getElementById('price').innerHTML =
                        '<span class="price-amount">' + data.product.formatted_price + '</span>';
                }
                btn.innerHTML = data.product.button_text;
                if(data.product.availability_button === 0){
                    btn.classList.add("btn-white");
                    btn.classList.remove("btn-primary");
                    btn.disabled = true;
                }else{
                    btn.classList.add("btn-primary");
                    btn.classList.remove("btn-white");
                    btn.disabled = false;
                }

                const shopsDiv = document.getElementById("shops-info");
                if(shopsDiv !== undefined && shopsDiv !== null) {
                    shopsDiv.innerHTML = "";
                    for (const shop of data.shops) {
                        shopsDiv.innerHTML += `<div class="pure-g mx-2">
                            <div class="pure-u-1-3 fw-bolder">${shop.city}</div>
                            <div class="pure-u-2-3">${shop.availability}</div>
                        </div>`;
                    }
                }

                const shopDiv = document.getElementById("shop-stock-info");
                if(shopDiv !== undefined && shopDiv !== null) {
                    shopDiv.innerHTML = "";
                    for (const shop of data.shops) {
                        if(shop.availability_button === 1){
                            shopDiv.innerHTML += "<div class=\"span round bg-green\"></div>" + shop.availability;
                        } else {
                            shopDiv.innerHTML += "<div class=\"span round bg-red\"></div>" + shop.availability;
                        }
                    }
                }

                let pRel = document.getElementsByClassName("p-recommends");
                if(pRel.length>0) {
                    pRel = pRel[0];
                    let tElement = pRel.getElementsByClassName('product')[0].cloneNode(true);

                    pRel.innerHTML = "";
                    for (const item of data.related.related) {
                        let e = tElement.cloneNode(true);
                        let b = e.getElementsByClassName("btn-add-cart")[0];
                        let oldId = b.dataset.id;
                        if(item.id !== oldId) {
                            b.dataset.id = item.id;
                            b.dataset.sku = item.sku;
                            b.dataset.price = item.price;

                            e.querySelector("a").href = "/product/" + item.product.id + "-" + item.product.slug[window.lan] + "/" + item.id;
                            e.querySelector(".price").innerHTML = item.formatted_price;

                            e.querySelector(".pn-" + oldId).innerHTML = item.product.name;
                            e.querySelector(".pn-" + oldId).classList.add("pn-" + item.id);
                            e.querySelector(".pn-" + oldId).classList.remove("pn-" + oldId);

                            e.querySelector(".pi-" + oldId).src = item.productImages[0];
                            e.querySelector(".pi-" + oldId).classList.add("pi-" + item.id);
                            e.querySelector(".pi-" + oldId).classList.remove("pi-" + oldId);

                            if (item.productImages.length > 1) {
                                e.querySelector(".picture-hover").src = item.productImages[1];
                            }
                        }
                        pRel.appendChild(e);
                    }
                    setTimeout(() => window.scrollx.resize(),100);
                }

                productPage.updateMix(data);
            });
    }
};

window.addEventListener('DOMContentLoaded', () => {
    productPage.load();
});
if( document.readyState !== 'loading' ) {
    productPage.load();
    productPage.loaded();
}
window.addEventListener('load', () => {
   productPage.loaded();
});
window.addEventListener('popstate',(event) => {
    productPage.navigate(event);
});
